import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { Box, InputBase, Paper, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/router';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MemoryIcon from '@mui/icons-material/Memory';
import FeedIcon from '@mui/icons-material/Feed';
import { Field } from '@models/CriteriaData';
import { SearchContext } from '../search/types';

interface QuickSearchOption {
  context: SearchContext;
  label: string;
  field: Field;
  placeholder: string;
  icon: JSX.Element;
}

const ICON_STYLE = { fontSize: 20, color: '#818cf8' };

const QUICK_SEARCH_OPTIONS: QuickSearchOption[] = [
  {
    context: 'web',
    label: 'Web',
    field: 'content',
    placeholder: 'Search the web…',
    icon: <Icon icon={'mdi:spider-web'} style={ICON_STYLE} />,
  },
  {
    context: 'images',
    label: 'Images',
    field: 'content',
    placeholder: 'Search images…',
    icon: <Icon icon={'ph:image-duotone'} style={ICON_STYLE} />,
  },
  {
    context: 'publications',
    label: 'Publications',
    field: 'title',
    placeholder: 'Search publications…',
    icon: <FeedIcon style={ICON_STYLE} />,
  },
  {
    context: 'organizations',
    label: 'Organizations',
    field: 'literal_name',
    placeholder: 'Search organizations…',
    icon: <AccountBalanceIcon style={ICON_STYLE} />,
  },
  {
    context: 'open-alex-people',
    label: 'People',
    field: 'query',
    placeholder: 'Search people…',
    icon: <Icon icon={'cil:people'} style={ICON_STYLE} />,
  },
  {
    context: 'people',
    label: 'Foreign Talent',
    field: 'query',
    placeholder: 'Search foreign talent…',
    icon: <PeopleAltIcon style={ICON_STYLE} />,
  },
  {
    context: 'technologies',
    label: 'Technologies',
    field: 'keywords',
    placeholder: 'Search technologies…',
    icon: <MemoryIcon style={ICON_STYLE} />,
  },
  {
    context: 'awards',
    label: 'Awards',
    field: 'query',
    placeholder: 'Search awards…',
    icon: <EmojiEventsIcon style={ICON_STYLE} />,
  },
];

interface QuickSearchFieldProps {
  option: QuickSearchOption;
}

function QuickSearchField({ option }: QuickSearchFieldProps) {
  const router = useRouter();
  const [query, setQuery] = useState<string>('');

  const handleSearch = () => {
    const q = query.trim();
    if (q.length === 0) {
      return;
    }

    router.push({
      pathname: `/search/${option.context}`,
      query: {
        criteria: JSON.stringify([
          {
            field: option.field,
            q,
            operator: option.context === 'organizations' ? 'AND' : 'OR',
            accuracy: 'Vague',
          },
        ]),
      },
    });
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Stack spacing={0.75} sx={{ flex: '1 1 150px', minWidth: 150 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {option.icon}
        <Typography
          sx={{
            color: '#e2e8f0',
            fontWeight: 600,
            fontSize: '0.85rem',
          }}
        >
          {option.label}
        </Typography>
      </Stack>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 2,
          px: 1,
          py: 0.25,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(129,140,248,0.25)',
          '&:focus-within': {
            border: '1px solid rgba(129,140,248,0.7)',
            boxShadow: '0 0 0 2px rgba(99,102,241,0.15)',
          },
        }}
      >
        <InputBase
          fullWidth
          value={query}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={option.placeholder}
          sx={{
            color: '#f8fafc',
            fontSize: '0.85rem',
            '& ::placeholder': { color: '#334155', opacity: 1 },
          }}
          inputProps={{ 'aria-label': `Search ${option.label}` }}
        />
        <Box
          component="button"
          type="button"
          onClick={handleSearch}
          disabled={query.trim().length === 0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            p: 0.5,
            color: '#818cf8',
            '&:disabled': { color: '#374151', cursor: 'default' },
          }}
        >
          <Icon icon="iconoir:nav-arrow-right" style={{ fontSize: 18 }} />
        </Box>
      </Paper>
    </Stack>
  );
}

export default function QuickSearchRow() {
  return (
    <Stack spacing={1.5}>
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: '#818cf8',
        }}
      >
        Quick Search
      </Typography>
      <Stack direction="row" flexWrap="wrap" sx={{ rowGap: 1.5, columnGap: 2 }}>
        {QUICK_SEARCH_OPTIONS.map((option) => (
          <QuickSearchField key={option.context} option={option} />
        ))}
      </Stack>
    </Stack>
  );
}
